import { ColumnDef } from '@tanstack/react-table'

import { CampaignColumn, CampaignColumnValue } from 'services/campaignColumns'
import { AICellRenderer } from './AICellRenderer'
import { AIColumnHeader } from './AIColumnHeader'
import { MANUAL_COLUMN_AGENT_TYPE } from './agentTypeMetadata'

interface ContactRow {
  id: number
}

const PREVIEW_ROW_COUNT = 5 // mirrors server/campaign_columns/constants.py

export function buildAIColumnDefs<TRow extends ContactRow>(
  columns: ReadonlyArray<CampaignColumn>,
  valuesByColumn: Record<number, CampaignColumnValue[]>,
  onRecompute: (columnId: number, includeEdited: boolean) => void,
  onSaveValue: (
    columnId: number,
    contactId: number,
    value: string
  ) => Promise<void>,
  onSaveBatchSettled: (columnId: number) => void,
  campaignId: number,
  recomputingColumnIds?: ReadonlySet<number>
): ColumnDef<TRow>[] {
  return columns.map((column) => {
    const isRecomputing = recomputingColumnIds?.has(column.id) ?? false
    const isManualColumn = column.engine_type === MANUAL_COLUMN_AGENT_TYPE
    const completedCount = column.progress.computed + column.progress.failed
    const previewTarget = Math.min(
      PREVIEW_ROW_COUNT,
      column.progress.total_contacts
    )
    const valuesByContact = new Map(
      (valuesByColumn[column.id] ?? []).map((v) => [v.contact, v])
    )
    return {
      id: `ai_column_${column.id}`,
      size: 340,
      minSize: 320,
      meta: {
        headerGroup: 'Custom columns',
        headerTitle: column.name,
        spreadsheetEditable: {
          getValue: (row: TRow) =>
            valuesByContact.get(row.id)?.value?.value ?? '',
          saveValue: (row: TRow, value: string) =>
            onSaveValue(column.id, row.id, value),
          afterSaveBatch: () => onSaveBatchSettled(column.id),
          canEdit: () => !isRecomputing,
        },
      },
      header: () => (
        <AIColumnHeader
          column={column}
          campaignId={campaignId}
          isRecomputing={isRecomputing}
          onRecompute={(includeEdited) => onRecompute(column.id, includeEdited)}
        />
      ),
      cell: ({ row }) => {
        const isPreviewRow = row.index < PREVIEW_ROW_COUNT
        const showLoadingForEmpty =
          !isManualColumn &&
          (isRecomputing ||
            (column.status === 'APPROVED' &&
              completedCount < column.progress.total_contacts) ||
            (column.status === 'PREVIEW' &&
              isPreviewRow &&
              completedCount < previewTarget))

        return (
          <AICellRenderer
            value={valuesByContact.get(row.original.id)}
            columnStatus={column.status}
            showLoadingForEmpty={showLoadingForEmpty}
            isManualColumn={isManualColumn}
            spreadsheetEditDisabled={isRecomputing}
          />
        )
      },
    }
  })
}
